import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AutoComplete } from "antd";
import { searchOceanServiceProviders } from "../../store/ManualQuote/OceanManualQuoteSlice";

const DEBOUNCE_MS = 350;

const INPUT_CLS =
  "h-[42px] px-3.5 focus:outline-none block w-full text-sm border border-gray-200 rounded-xl text-gray-800 bg-white transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 hover:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed disabled:hover:border-gray-100";

// Field names are unconfirmed for this endpoint (no sample response yet) —
// try the common variants rather than assume one, same defensive approach
// used for ports before that shape was confirmed.
const providerLabel = (p) => {
  const name = p?.name || p?.provider_name || p?.company_name || p?.transporter_name || "Unnamed";
  const country = p?.country_code || p?.country;
  const connectionType = p?.connection_type || p?.connectionType || p?.type;
  return `${name}${country ? ` (${country})` : ""}${connectionType ? ` | ${connectionType}` : ""}`;
};
const providerValue = (p) => p?.name || p?.provider_name || p?.company_name || p?.transporter_name || p?._id;

// Pickup/Delivery Transporter field backed by
// GET ocean-manual-quote/get-service-providers?query=...&limit=50&skip=0 —
// fetches an initial browsable list on mount (query="") since the confirmed
// request shape supports that, then re-fetches per keystroke. Uses
// AutoComplete rather than Select — behaves like a plain text field; the
// API's matches are offered as suggestions but typing something that
// doesn't match anything is still accepted as the field value.
const AsyncTransporterSelect = ({ name, value, setFieldValue, placeholder, disabled, width = "100%" }) => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const debounceRef = useRef(null);

  const runSearch = (text) => {
    dispatch(searchOceanServiceProviders(text)).then((res) => {
      const rows = res?.payload?.data?.data || res?.payload?.data || [];
      setOptions(rows.map((p) => ({ value: providerValue(p), label: providerLabel(p) })));
    });
  };

  useEffect(() => {
    if (!disabled) runSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const handleSearch = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text.trim()), DEBOUNCE_MS);
  };

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  return (
    <AutoComplete
      value={value || ""}
      options={options}
      onSearch={handleSearch}
      onChange={(val) => setFieldValue(name, val)}
      onSelect={(val) => setFieldValue(name, val)}
      disabled={disabled}
      filterOption={false}
      style={{ width }}
      popupMatchSelectWidth={false}
    >
      <input type="text" placeholder={placeholder} className={INPUT_CLS} />
    </AutoComplete>
  );
};

export default AsyncTransporterSelect;
